import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Tooltip,
  Divider,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  PhoneIphone as PhoneIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  LocalOffer as TagIcon,
  LocationOn as LocationIcon,
  ArrowForward as ArrowForwardIcon,
  Visibility as ViewIcon,
  ContentCopy as ContentCopyIcon,
  FilterAltOff as FilterAltOffIcon,
  Campaign as CampaignIcon,
  Smartphone as SmartphoneIcon,
  Check as CheckIcon,
  ChevronLeft as ChevronLeftIcon,
  OpenInNew as OpenInNewIcon,
  Flag as FlagIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon,
  ViewAgenda as CardViewIcon,
  CloseRounded as CloseRoundedIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import { promotionsAPI, citiesAPI } from '../services/api';

/** Color palette constants matching Contacts.js and Portal Theme */
const GS_COLORS = {
  primary: '#1E3A45',
  orange: '#FF7A29',
  orangeDark: '#E0621B',
  orangeLight: '#FFF5EF',
  textPrimary: '#1F2937',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  borderDark: 'rgba(30, 58, 69, 0.22)',
  bgLight: '#F9FAFB',
  white: '#FFFFFF',
};

const PROMOTION_TYPES = [
  'Discount',
  'Special Offer',
  'Event',
  'Banner',
  'Voucher',
  'General'
];

const PROMOTION_FIELD_MAX_LENGTHS = {
  title: 100,
  description: 500,
  details: 250,
  discountText: 50,
  couponTitle: 50,
  termsAndConditions: 150,
};
const MAX_WHAT_YOU_GET_LENGTH = 200;
const TARGET_AUDIENCES = [
  { value: 'All Users', translationKey: 'all-users' },
  { value: 'Students', translationKey: 'students' },
  { value: 'Families', translationKey: 'families' },
  { value: 'Workers', translationKey: 'workers' },
];

const LOCAL_STORAGE_KEY = 'getsettled_promotions_data';

const EMPTY_PROMOTION_FORM = {
  id: '',
  title: '',
  description: '',
  details: '',
  cityId: '1',
  cityName: 'Berlin',
  type: '',
  promotionTypeId: '',
  bannerImage: '',
  discountText: '',
  discountCoupon: '',
  couponTitle: '',
  promotionDate: new Date().toISOString().split('T')[0],
  promotionTime: '14:00',
  webUrl: '',
  sharingUrl: '',
  ownerName: '',
  termsAndConditions: '',
  targetedAudience: '',
  callToAction: '',
  whatYouGet: [],
  pictures: [],
  picturesKeep: [],
  picturesRemove: [],
  active: true
};

const DEFAULT_PROMOTIONS = [];

const formFieldSx = {
  '& .MuiInputLabel-root': {
    color: GS_COLORS.textPrimary,
    fontWeight: 500,
    '&.Mui-focused': { color: GS_COLORS.orange }
  },
  '& .MuiFormLabel-asterisk': {
    color: '#d32f2f !important',
    fontWeight: 700,
  },
  '& .MuiInputLabel-asterisk': {
    color: '#d32f2f !important',
    fontWeight: 700,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#fff',
    color: GS_COLORS.textPrimary,
    '& fieldset': { borderColor: GS_COLORS.borderDark },
    '&:hover fieldset': { borderColor: 'rgba(30, 58, 69, 0.38)' },
    '&.Mui-focused fieldset': { borderColor: GS_COLORS.orange, borderWidth: 2 }
  }
};

/** Helper URL validator function */
function isValidUrlFormat(str) {
  if (!str) return true;
  try {
    const testStr = str.startsWith('http://') || str.startsWith('https://') ? str : `https://${str}`;
    const url = new URL(testStr);
    return Boolean(url.hostname && url.hostname.includes('.'));
  } catch (e) {
    return false;
  }
}

/** Helper validation function supporting i18n */
function validatePromotionField(name, value, t) {
  const val = String(value ?? '').trim();
  const maxLength = PROMOTION_FIELD_MAX_LENGTHS[name];
  if (maxLength && String(value ?? '').length > maxLength) {
    return t
      ? t('pages.promotions.validation.maxLength', { max: maxLength })
      : `Must be ${maxLength} characters or fewer`;
  }
  switch (name) {
    case 'title':
      if (!val) return t ? t('pages.promotions.validation.titleRequired') : 'Promotion Title is required';
      if (val.length < 3) return t ? t('pages.promotions.validation.titleMinLength') : 'Title must be at least 3 characters';
      return '';
    case 'description':
      if (!val) return t ? t('pages.promotions.validation.descriptionRequired') : 'Description is required';
      return '';
    case 'cityName':
    case 'cityId':
      if (!val) return t ? t('pages.promotions.validation.cityRequired') : 'City is required';
      return '';
    case 'type':
    case 'promotionTypeId':
      if (!val) return t ? t('pages.promotions.validation.typeRequired') : 'Promotion Type is required';
      return '';
    case 'promotionDate':
      if (!val) return t ? t('pages.promotions.validation.dateRequired') : 'Promotion Date is required';
      if (val < new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)) return 'Promotion date cannot be in the past.';
      return '';
    case 'promotionTime':
      if (!val) return t ? t('pages.promotions.validation.timeRequired') : 'Promotion Time is required';
      return '';
    case 'webUrl':
      if (val && !isValidUrlFormat(val)) return t ? t('pages.promotions.validation.webUrlInvalid') : 'Please enter a valid URL (e.g. https://example.com/offer)';
      return '';
    case 'sharingUrl':
      if (val && !isValidUrlFormat(val)) return t ? t('pages.promotions.validation.sharingUrlInvalid') : 'Please enter a valid URL (e.g. https://example.com/share)';
      return '';
    default:
      return '';
  }
}

/** Format Promotion Type with i18n support */
function formatPromoType(type, t) {
  if (!type) return '';
  if (!t) return String(type);
  const rawStr = String(type).trim();
  const cleanType = rawStr.toLowerCase().replace(/[\s_]+/g, '');
  const key = `pages.promotions.type.${cleanType}`;
  const translated = t(key);
  return translated !== key ? translated : rawStr;
}

/** Extract & resolve promotion type name from any promo object property or ID */
function getPromoTypeName(promo, promotionTypes, t) {
  if (!promo) return t ? t('pages.promotions.card.defaultType') : 'Promotion';

  let rawVal =
    promo.type ||
    promo.promotionType ||
    promo.promotionTypeName ||
    promo.typeName ||
    promo.PromotionType ||
    '';

  if (typeof rawVal === 'object' && rawVal !== null) {
    rawVal = rawVal.name || rawVal.type || rawVal.label || rawVal.title || '';
  }

  let rawId =
    promo.promotionTypeId ||
    promo.PromotionTypeId ||
    promo.typeId ||
    '';

  if (rawId && Array.isArray(promotionTypes)) {
    const found = promotionTypes.find((item) => {
      if (typeof item === 'object' && item !== null) {
        return String(item.id) === String(rawId) || String(item.typeId) === String(rawId);
      }
      return false;
    });
    if (found) {
      const typeStr = typeof found === 'object' ? (found.name || found.type || found.label || String(rawVal)) : String(found);
      if (typeStr) rawVal = typeStr;
    }
  }

  if (rawVal && !isNaN(Number(rawVal)) && Array.isArray(PROMOTION_TYPES)) {
    const idx = parseInt(rawVal, 10) - 1;
    if (PROMOTION_TYPES[idx]) {
      rawVal = PROMOTION_TYPES[idx];
    }
  }

  if (!rawVal) {
    return t ? t('pages.promotions.card.defaultType') : 'Promotion';
  }

  return formatPromoType(rawVal, t);
}

/** Filter helper matching Contacts.js filtering pattern */
function promotionMatchesSearch(promo, searchText) {
  if (!searchText) return true;
  const term = searchText.toLowerCase();
  return (
    (promo.title || '').toLowerCase().includes(term) ||
    (promo.description || '').toLowerCase().includes(term) ||
    (promo.discountCoupon || '').toLowerCase().includes(term)
  );
}

function filterPromotionsList(promotions, cityFilter, typeFilter, statusFilter, searchText) {
  return promotions.filter((p) => {
    const matchSearch = promotionMatchesSearch(p, searchText);
    const matchCity = cityFilter === 'all' || p.cityName === cityFilter || p.cityId === cityFilter;
    const matchType = typeFilter === 'all' || p.type === typeFilter;
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && p.active) ||
      (statusFilter === 'inactive' && !p.active);
    return matchSearch && matchCity && matchType && matchStatus;
  });
}

function getPromoDateDisplay(promo) {
  if (!promo) return '';
  const d =
    promo.promotionDate ||
    promo.PromotionDate ||
    promo.startDate ||
    promo.StartDate ||
    promo.createdDate ||
    promo.date ||
    '';
  if (!d) return '';
  if (typeof d === 'string') {
    if (d.includes('T')) return d.split('T')[0];
    return d;
  }
  try {
    const parsed = new Date(d);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
  } catch (e) { }
  return String(d);
}

function getPromoTimeDisplay(promo) {
  if (!promo) return '';
  const t =
    promo.promotionTime ||
    promo.PromotionTime ||
    promo.time ||
    '';
  return String(t || '');
}

function getCtaForLocale(promo, locale) {
  if (!promo) return '';
  if (locale === 'de') {
    return promo.callToActionDe || promo.callToAction || promo.callToActionEn || '';
  }
  return promo.callToActionEn || promo.callToAction || promo.callToActionDe || '';
}

function getPromoApprovalStatus(promo, t) {
  if (!promo) return { raw: 'APPROVED', label: t ? t('pages.promotions.status.approved') : 'APPROVED' };
  const val = promo.approvalStatus ?? promo.ApprovalStatus ?? promo.statusName ?? promo.status;
  let raw = 'APPROVED';
  if (val !== undefined && val !== null) {
    if (typeof val === 'number') {
      if (val === 1) raw = 'APPROVED';
      else if (val === 0) raw = 'PENDING';
      else if (val === 2) raw = 'REJECTED';
    } else {
      const str = String(val).trim().toUpperCase();
      if (str.includes('REJECT')) raw = 'REJECTED';
      else if (str.includes('PEND') || str.includes('INACTIV')) raw = 'PENDING';
      else if (str.includes('APPROV') || str.includes('ACTIV')) raw = 'APPROVED';
      else raw = str;
    }
  } else {
    raw = promo.active ? 'APPROVED' : 'PENDING';
  }

  let label = raw;
  if (t) {
    if (raw === 'APPROVED') label = t('pages.promotions.status.approved');
    else if (raw === 'PENDING') label = t('pages.promotions.status.pending');
    else if (raw === 'REJECTED') label = t('pages.promotions.status.rejected');
  }

  return { raw, label };
}

function getAllPromoImages(promo) {
  if (!promo) return [];
  const list = [];

  const isValidImg = (url) => {
    if (!url || typeof url !== 'string') return false;
    const str = url.trim().toLowerCase();
    return (
      str.startsWith('http://') ||
      str.startsWith('https://') ||
      str.startsWith('data:image/') ||
      str.startsWith('blob:') ||
      str.startsWith('/') ||
      /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(str)
    );
  };

  if (Array.isArray(promo.pictures)) {
    promo.pictures.forEach((pic) => {
      if (typeof pic === 'string' && isValidImg(pic)) list.push(pic);
      else if (typeof File !== 'undefined' && pic instanceof File) list.push(URL.createObjectURL(pic));
      else if (pic && typeof pic === 'object') {
        const u = pic.url || pic.path || pic.pictureUrl || pic.picture;
        if (isValidImg(u)) list.push(u);
      }
    });
  }

  if (isValidImg(promo.bannerImage) && !list.includes(promo.bannerImage)) {
    list.unshift(promo.bannerImage);
  }

  const singlePic = promo.pictureUrl || promo.picture || promo.image;
  if (isValidImg(singlePic) && !list.includes(singlePic)) {
    list.push(singlePic);
  }

  if (Array.isArray(promo.images)) {
    promo.images.forEach((img) => {
      const u = typeof img === 'string' ? img : (img?.url || img?.path);
      if (isValidImg(u) && !list.includes(u)) list.push(u);
    });
  }

  return list;
}

function PromoCardGallery({ promo, t }) {
  const imagesList = getAllPromoImages(promo);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = React.useRef(null);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const width = scrollRef.current.clientWidth;
    if (width > 0) {
      const newIndex = Math.round(scrollRef.current.scrollLeft / width);
      if (newIndex !== activeIndex && newIndex >= 0 && newIndex < imagesList.length) {
        setActiveIndex(newIndex);
      }
    }
  };

  const scrollToImage = (index) => {
    if (scrollRef.current) {
      const width = scrollRef.current.clientWidth;
      scrollRef.current.scrollTo({
        left: index * width,
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  if (imagesList.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1E3A45 0%, #2D5868 100%)',
          color: '#fff',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        <TagIcon sx={{ fontSize: 38, color: '#FF7A29', opacity: 0.9 }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: 0.5 }}>
          {t('pages.promotions.card.defaultBanner')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          height: 180,
          width: '100%',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
      >
        {imagesList.map((imgUrl, idx) => (
          <Box
            key={idx}
            sx={{
              minWidth: '100%',
              width: '100%',
              height: '100%',
              flexShrink: 0,
              scrollSnapAlign: 'start',
              position: 'relative'
            }}
          >
            <CardMedia
              component="img"
              height="180"
              image={imgUrl}
              alt={`${promo.title || 'Promotion'}`}
              sx={{ objectFit: 'cover', width: '100%', height: '100%' }}
            />
          </Box>
        ))}
      </Box>

      {/* Dots Indicator at bottom center when there are multiple images */}
      {imagesList.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(4px)',
            px: 1.2,
            py: 0.5,
            borderRadius: '12px',
            zIndex: 4
          }}
        >
          {imagesList.map((_, idx) => (
            <Box
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                scrollToImage(idx);
              }}
              sx={{
                width: activeIndex === idx ? 16 : 6,
                height: 6,
                borderRadius: '4px',
                backgroundColor: activeIndex === idx ? '#FF7A29' : 'rgba(255, 255, 255, 0.65)',
                cursor: 'pointer',
                transition: 'all 0.25s ease-in-out',
                '&:hover': {
                  backgroundColor: '#FF7A29'
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}


function PromotionCardViewDialog({ promo, open, onClose, onOpenMobilePreview, t, promotionTypes, locale }) {
  const imagesList = getAllPromoImages(promo);

  if (!promo) return null;

  const promoTypeName = getPromoTypeName(promo, promotionTypes, t);
  const cardViewTitle = t && t('pages.promotions.card.cardView') !== 'pages.promotions.card.cardView' ? t('pages.promotions.card.cardView') : 'Card View';

  const bannerImg = imagesList.length > 0 ? imagesList[0] : (promo.bannerImages || promo.imageUrls || promo.image || 'https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800&auto=format&fit=crop');

  const discountBadgeText = promo.discountText || promo.title || promoTypeName || 'Hot Promotion';
  const ctaText = getCtaForLocale(promo, locale) || promo.callToAction || promo.ctaText || 'Hot Deal';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 520,
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }
      }}
    >
      {/* Cross (Close) Icon positioned at top-right */}
      <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#ffffff',
            bgcolor: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(4px)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* App-Side Promotion Listing Card (Exact Match to Mobile App Image 2) */}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 480,
          height: 235,
          borderRadius: '24px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 45px rgba(0, 0, 0, 0.28)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            bgcolor: '#1E3A45',
          }}
        >
          {/* Background Image */}
          <Box
            component="img"
            src={bannerImg}
            alt={promo.title}
            onError={(e) => {
              e.currentTarget.src = 'https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800&auto=format&fit=crop';
            }}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />

          {/* Dark Vignette / Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.05) 40%, rgba(0,0,0,0.85) 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* Top-Right Pill: ðŸ”¥ Call To Action */}
          <Box
            sx={{
              position: 'absolute',
              top: 14,
              right: 14,
              zIndex: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              bgcolor: 'rgba(30, 41, 59, 0.75)',
              backdropFilter: 'blur(10px)',
              color: '#FFFFFF',
              px: 1.4,
              py: 0.5,
              borderRadius: '999px',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              fontSize: '0.82rem',
              fontWeight: 800,
              letterSpacing: '0.2px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            }}
          >
            <span>{'\uD83D\uDD25'}</span>
            <span>{ctaText}</span>
          </Box>

          {/* Center-Left Orange Pill (Discount Headline) */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: 14,
              transform: 'translateY(-50%)',
              zIndex: 2,
              maxWidth: '85%',
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: '#FF9500',
                color: '#FFFFFF',
                px: 2,
                py: 0.7,
                borderRadius: '999px',
                fontWeight: 850,
                fontSize: '0.92rem',
                letterSpacing: '0.2px',
                boxShadow: '0 4px 14px rgba(255, 149, 0, 0.45)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {discountBadgeText}
            </Box>
          </Box>

          {/* Bottom-Left Title & Subtitle */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 14,
              left: 14,
              right: 14,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Typography
              sx={{
                fontWeight: 850,
                color: '#FFFFFF',
                fontSize: '1.35rem',
                lineHeight: 1.2,
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.9)',
              }}
              noWrap
            >
              {promo.title || 'Special Promotion'}
            </Typography>
            <Typography
              sx={{
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.92rem',
                mt: 0.35,
                textShadow: '0 2px 6px rgba(0, 0, 0, 0.9)',
              }}
              noWrap
            >
              {promo.ownerName || promo.description || 'GetSettled Partner'}
            </Typography>
          </Box>
        </Paper>

      {/* Switch to Full Mobile Screen Button */}
      <Button
        fullWidth
        variant="contained"
        size="small"
        onClick={() => {
          onClose();
          if (onOpenMobilePreview) {
            onOpenMobilePreview(promo);
          }
        }}
        endIcon={<SmartphoneIcon sx={{ fontSize: 16 }} />}
        sx={{
          mt: 2,
          maxWidth: 480,
          borderRadius: '14px',
          bgcolor: '#063937',
          color: '#fff',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.85rem',
          py: 1.1,
          boxShadow: '0 8px 24px rgba(6, 57, 55, 0.35)',
          '&:hover': { bgcolor: '#042725' },
        }}
      >
        Open Full Mobile Screen
      </Button>
    </Dialog>
  );
}
function PromotionMobileMockupDialog({ promo, open, onClose, t, promotionTypes, locale }) {
  const imagesList = getAllPromoImages(promo);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    setActiveSlideIndex(0);
  }, [promo]);

  if (!promo) return null;

  const handleScroll = (e) => {
    const el = e.target;
    const width = el.clientWidth;
    if (width > 0) {
      const idx = Math.round(el.scrollLeft / width);
      if (idx !== activeSlideIndex && idx >= 0 && idx < imagesList.length) {
        setActiveSlideIndex(idx);
      }
    }
  };

  const scrollToImage = (index) => {
    if (scrollRef.current) {
      const width = scrollRef.current.clientWidth;
      scrollRef.current.scrollTo({
        left: index * width,
        behavior: 'smooth'
      });
      setActiveSlideIndex(index);
    }
  };

  const copyCouponCode = (code) => {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      toast.success(t ? t('pages.promotions.toast.couponCopied', { code }) : `Coupon ${code} copied!`);
    } catch (e) {
      toast.info(`Coupon: ${code}`);
    }
  };

  const promoTypeName = getPromoTypeName(promo, promotionTypes, t);
  const ctaText = getCtaForLocale(promo, locale) || promo.callToAction || promo.ctaText || '';
  const rawWebUrl = promo.webUrl || promo.WebUrl || '';
  const webUrlFormatted = rawWebUrl ? (rawWebUrl.startsWith('http') ? rawWebUrl : `https://${rawWebUrl}`) : '';
  const domainName = rawWebUrl ? rawWebUrl.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0] : 'allianz-stoiberlipp.de';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: -25,
          right: 0,
          color: '#fff',
          bgcolor: 'rgba(0,0,0,0.5)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Phone Frame Container */}
      <Box
        sx={{
          width: 330,
          height: 700,
          borderRadius: '40px',
          border: '12px solid #1E293B',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
          bgcolor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Scrollable Phone Inner Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            bgcolor: '#FFFFFF',
            pb: '68px',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '4px' },
          }}
        >
          {/* Header Banner Carousel Section */}
          <Box sx={{ position: 'relative', width: '100%', height: 210, bgcolor: '#1E3A45', overflow: 'hidden' }}>
            {imagesList.length > 0 ? (
              <Box
                ref={scrollRef}
                onScroll={handleScroll}
                sx={{
                  display: 'flex',
                  overflowX: 'auto',
                  width: '100%',
                  height: '100%',
                  scrollSnapType: 'x mandatory',
                  scrollBehavior: 'smooth',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {imagesList.map((imgUrl, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      minWidth: '100%',
                      width: '100%',
                      height: '100%',
                      flexShrink: 0,
                      scrollSnapAlign: 'start',
                      position: 'relative'
                    }}
                  >
                    <Box
                      component="img"
                      src={imgUrl}
                      alt={`Banner ${idx + 1}`}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)',
                        pointerEvents: 'none'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #1E3A45 0%, #2D5868 100%)',
                  color: '#fff',
                }}
              >
                <TagIcon sx={{ fontSize: 40, color: '#FF7A29', opacity: 0.8 }} />
              </Box>
            )}

            {/* Back Button (<) */}
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 14,
                left: 12,
                zIndex: 6,
                width: 30,
                height: 30,
                bgcolor: 'rgba(255, 255, 255, 0.85)',
                color: '#1F2937',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': { bgcolor: '#FFFFFF' }
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Tag Pill + Title Overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 14,
                left: 14,
                right: 75,
                zIndex: 6,
                pointerEvents: 'none'
              }}
            >
              <Box
                sx={{
                  display: 'inline-block',
                  bgcolor: '#FF7A29',
                  color: '#FFFFFF',
                  fontSize: '9.5px',
                  fontWeight: 800,
                  px: 1.2,
                  py: 0.3,
                  borderRadius: '12px',
                  mb: 0.4,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {promo.discountText || promoTypeName || 'New Customer Cashback'}
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#FFFFFF',
                  fontSize: '1.15rem',
                  lineHeight: 1.2,
                  textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                  wordBreak: 'break-word'
                }}
              >
                {promo.title || 'Cashback'}
              </Typography>
            </Box>

            {/* Pagination Dots and 1/4 Counter */}
            {imagesList.length > 1 && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 14,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 6
                  }}
                >
                  {imagesList.map((_, idx) => (
                    <Box
                      key={idx}
                      onClick={() => scrollToImage(idx)}
                      sx={{
                        width: activeSlideIndex === idx ? 14 : 5,
                        height: 5,
                        borderRadius: '3px',
                        bgcolor: activeSlideIndex === idx ? '#FF7A29' : 'rgba(255, 255, 255, 0.7)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    />
                  ))}
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 14,
                    right: 12,
                    zIndex: 6,
                    bgcolor: 'rgba(0, 0, 0, 0.65)',
                    color: '#FFFFFF',
                    px: 1,
                    py: 0.3,
                    borderRadius: '10px',
                    fontSize: '9.5px',
                    fontWeight: 800,
                    letterSpacing: 0.5
                  }}
                >
                  {activeSlideIndex + 1}/{imagesList.length}
                </Box>
              </>
            )}
          </Box>

          {/* Main Body Content */}
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.8 }}>

            {/* Call to Action / WhatsApp Box */}
            <Box
              sx={{
                bgcolor: '#FFF8F0',
                border: '1.5px solid #FFC899',
                borderRadius: '12px',
                p: 1.2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                boxShadow: '0 2px 8px rgba(255, 122, 41, 0.06)'
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: '#FFF0E5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <CampaignIcon sx={{ color: '#FF7A29', fontSize: 17 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, color: '#D96515', fontSize: '0.78rem', lineHeight: 1.3 }}>
                  {ctaText || 'Write me with WhatsApp'}
                </Typography>
                {(promo.whatsapp || promo.phone) && (
                  <Typography sx={{ fontWeight: 700, color: '#D96515', fontSize: '0.75rem', lineHeight: 1.3 }}>
                    {promo.whatsapp || promo.phone}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Section 1: What You Get */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                <Box sx={{ width: 3, height: 14, bgcolor: '#FF7A29', borderRadius: '2px' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#1F2937' }}>
                  What You Get
                </Typography>
              </Box>

              <Stack spacing={0.8}>
                {Array.isArray(promo.whatYouGet) && promo.whatYouGet.length > 0 ? (
                  promo.whatYouGet.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        bgcolor: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: '10px',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: '#D1FAE5',
                          color: '#059669',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 12, fontWeight: 900 }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                        {item}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      bgcolor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '10px',
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: '#D1FAE5',
                        color: '#059669',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 12, fontWeight: 900 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>
                      {promo.discountText || '50€ back after contract'}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Section 2: Description */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                <Box sx={{ width: 3, height: 14, bgcolor: '#FF7A29', borderRadius: '2px' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#1F2937' }}>
                  Description
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', lineHeight: 1.5, wordBreak: 'break-word' }}>
                {promo.description || 'for every new client who make a first time contract with Thorsten Stoiber-Lipp'}
              </Typography>
            </Box>

            {/* Section 3: Promotion Details */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                <Box sx={{ width: 3, height: 14, bgcolor: '#FF7A29', borderRadius: '2px' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#1F2937' }}>
                  Promotion Details
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  p: 1.2
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: 500 }}>
                  {promo.details || promo.discountText || promo.title || 'New Customer Cashback'}
                </Typography>
              </Box>
            </Box>

            {/* Section 4: Terms & Conditions */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                <Box sx={{ width: 3, height: 14, bgcolor: '#FF7A29', borderRadius: '2px' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#1F2937' }}>
                  Terms & Conditions
                </Typography>
              </Box>
              <Box
                onClick={() => webUrlFormatted && window.open(webUrlFormatted, '_blank')}
                sx={{
                  bgcolor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  p: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: webUrlFormatted ? 'pointer' : 'default',
                  mb: 0.8,
                  '&:hover': webUrlFormatted ? { bgcolor: '#F3F4F6' } : {}
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }} noWrap>
                  {domainName}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#1E3A45', fontWeight: 800, whiteSpace: 'nowrap' }}>
                  View &gt;
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.68rem', color: '#9CA3AF', lineHeight: 1.35 }}>
                {promo.termsAndConditions || "Offer subject to provider's terms. Welivin facilitates discovery only and is not party to this promotion."}
              </Typography>
            </Box>

            {/* Section 5: Target Audience & Owner Name Card */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '14px',
                p: 1.4,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.2
              }}
            >
              {/* Target Audience Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    bgcolor: '#EEF2FF',
                    color: '#6366F1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600 }}>
                    Target Audience
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#111827', fontWeight: 800 }} noWrap>
                    {TARGET_AUDIENCES.some((audience) => audience.value === promo.targetedAudience)
                      ? t(`pages.promotions.audience.${TARGET_AUDIENCES.find((audience) => audience.value === promo.targetedAudience).translationKey}`)
                      : promo.targetedAudience || t('pages.promotions.audience.all-users')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ borderColor: '#E5E7EB' }} />

              {/* Owner Name Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    bgcolor: '#F5F3FF',
                    color: '#8B5CF6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <PersonIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', fontWeight: 600 }}>
                    Owner Name
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#111827', fontWeight: 800 }} noWrap>
                    {promo.ownerName || 'Thorsten Stoiber-Lipp'}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Report Promotion Link */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.8,
                py: 0.8,
                cursor: 'pointer',
                color: '#6B7280',
                '&:hover': { color: '#374151' }
              }}
            >
              <FlagIcon sx={{ fontSize: 15 }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                Report Promotion
              </Typography>
            </Box>

          </Box>
        </Box>

        {/* Fixed Bottom Action Bar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 1.2,
            bgcolor: '#FFFFFF',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            gap: 1,
            zIndex: 15,
            boxShadow: '0 -4px 16px rgba(0,0,0,0.06)'
          }}
        >
          <Button
            fullWidth
            onClick={() => copyCouponCode(promo.discountCoupon || 'GET50')}
            sx={{
              height: 40,
              borderRadius: '10px',
              bgcolor: '#FFF8F0',
              border: '1.5px solid #FFC899',
              color: '#D96515',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.8,
              p: 0.8,
              '&:hover': { bgcolor: '#FFEFE0', borderColor: '#FF7A29' }
            }}
          >
            <TagIcon sx={{ fontSize: 15, color: '#D96515' }} />
            <Box sx={{ textAlign: 'left', lineHeight: 1.1 }}>
              <Typography sx={{ fontSize: '8.5px', fontWeight: 700, color: '#D96515' }}>
                Copy Code
              </Typography>
              <Typography sx={{ fontSize: '11px', fontWeight: 900, color: '#D96515' }}>
                {promo.discountCoupon || 'GET50'}
              </Typography>
            </Box>
          </Button>

          <Button
            fullWidth
            onClick={() => webUrlFormatted && window.open(webUrlFormatted, '_blank')}
            sx={{
              height: 40,
              borderRadius: '10px',
              bgcolor: '#1E3A45',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.78rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.8,
              boxShadow: '0 3px 10px rgba(30, 58, 69, 0.2)',
              '&:hover': { bgcolor: '#2D5868' }
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 15 }} />
            Go To Website
          </Button>
        </Box>

        {/* Bottom Phone Bar */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 100,
            height: 3.5,
            bgcolor: '#CBD5E1',
            borderRadius: '2px',
            zIndex: 20
          }}
        />
      </Box>
    </Dialog>
  );
}

export default function Promotions() {
  const { t, locale } = useLanguage();

  // State
  const [promotions, setPromotions] = useState([]);
  const [cities, setCities] = useState([]);
  const [promotionTypes, setPromotionTypes] = useState(PROMOTION_TYPES);
  const [loading, setLoading] = useState(false);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form & Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(EMPTY_PROMOTION_FORM);
  const [formErrors, setFormErrors] = useState({});

  // Delete Confirmation Dialog State
  const [deleteId, setDeleteId] = useState(null);

  // Detail View Dialog State
  const [viewPromo, setViewPromo] = useState(null);

  // Mobile Mockup Preview Dialog State
  const [mobilePreviewPromo, setMobilePreviewPromo] = useState(null);

  // Card View Preview Dialog State
  const [cardPreviewPromo, setCardPreviewPromo] = useState(null);

  // Dynamic "What You Get" input field
  const [newGetItem, setNewGetItem] = useState('');
  const [editingGetItemIndex, setEditingGetItemIndex] = useState(null);
  const [editingGetItemValue, setEditingGetItemValue] = useState('');

  // Copy Coupon Helper
  const handleCopyCoupon = (code) => {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      toast.success(t('pages.promotions.toast.couponCopied', { code }));
    } catch (e) {
      toast.info(`Coupon: ${code}`);
    }
  };

  // Save local fallback storage helper (sessionStorage so tab close clears it)
  const saveToLocal = useCallback((items) => {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } catch (e) {
      console.warn('Failed to save promotions to sessionStorage', e);
    }
  }, []);

  // Fetch Cities
  const loadCities = useCallback(async () => {
    try {
      const res = await citiesAPI.getCities();
      const list = res.data?.data || res.data || [];
      if (Array.isArray(list) && list.length > 0) {
        setCities(list);
      } else {
        setCities([{ id: 'berlin-01', name: 'Berlin' }, { id: 'munich-02', name: 'Munich' }, { id: 'hamburg-03', name: 'Hamburg' }]);
      }
    } catch (e) {
      setCities([{ id: 'berlin-01', name: 'Berlin' }, { id: 'munich-02', name: 'Munich' }, { id: 'hamburg-03', name: 'Hamburg' }]);
    }
  }, []);

  // Fetch Localized Promotion Types (5th Image Endpoint: GET /api/customer/promotions/types)
  const loadPromotionTypes = useCallback(async () => {
    try {
      const res = await promotionsAPI.getPromotionTypes(locale || 'en');
      const list = res.data?.data || res.data || [];
      if (Array.isArray(list) && list.length > 0) {
        setPromotionTypes(list);
      }
    } catch (e) {
      // fallback to default PROMOTION_TYPES
    }
  }, [locale]);

  // Fetch Promotions (1st Image Endpoint: GET /api/customer/promotions)
  const loadPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await promotionsAPI.getPromotions({
        cityId: cityFilter !== 'all' ? (Number(cityFilter) || cityFilter) : 0,
        page: 1,
        pageSize: 50,
        search: searchTerm,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined
      });
      const data = res.data?.data || res.data;
      if (Array.isArray(data)) {
        setPromotions(data);
        saveToLocal(data);
      } else {
        const stored =
          (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOCAL_STORAGE_KEY)) ||
          (typeof localStorage !== 'undefined' && localStorage.getItem(LOCAL_STORAGE_KEY));
        if (stored) {
          setPromotions(JSON.parse(stored));
        } else {
          setPromotions([]);
        }
      }
    } catch (e) {
      const stored =
        (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(LOCAL_STORAGE_KEY)) ||
        (typeof localStorage !== 'undefined' && localStorage.getItem(LOCAL_STORAGE_KEY));
      if (stored) {
        setPromotions(JSON.parse(stored));
      } else {
        setPromotions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, cityFilter, typeFilter, statusFilter, saveToLocal]);

  useEffect(() => {
    loadCities();
    loadPromotionTypes();
    loadPromotions();
  }, [loadCities, loadPromotionTypes, loadPromotions]);

  // Fetch One Particular Promotion (3rd Image Endpoint: GET /api/customer/promotions/{promotionId})
  const handleViewDetails = async (promo) => {
    try {
      const res = await promotionsAPI.getPromotion(promo.id);
      const detailData = res.data?.data || res.data;
      if (detailData && typeof detailData === 'object') {
        setViewPromo(detailData);
      } else {
        setViewPromo(promo);
      }
    } catch (e) {
      setViewPromo(promo);
    }
  };

  // Handle Form Input Change & Field Validation
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    const maxLength = PROMOTION_FIELD_MAX_LENGTHS[name];
    const val = type === 'checkbox' ? checked : maxLength ? value.slice(0, maxLength) : value;
    setFormData((prev) => ({
      ...prev,
      [name]: val
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: validatePromotionField(name, val, t)
      }));
    }
  };

  // Image Upload Handler (Single Banner)
  const handleBannerUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          bannerImage: reader.result,
          pictures: [...(prev.pictures || []), file]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBannerImage = () => {
    setFormData((prev) => ({ ...prev, bannerImage: '' }));
  };

  // Multiple Pictures Upload Handler
  const handlePicturesUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        pictures: [...(prev.pictures || []), ...files]
      }));
    }
  };

  const removePictureKeepItem = (index) => {
    setFormData((prev) => {
      const kept = prev.picturesKeep || [];
      if (index < 0 || index >= kept.length) return prev;
      const removedItem = kept[index];
      return {
        ...prev,
        picturesKeep: kept.filter((_, i) => i !== index),
        picturesRemove: [...(prev.picturesRemove || []), removedItem]
      };
    });
  };

  const removePictureItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      pictures: (prev.pictures || []).filter((_, i) => i !== index)
    }));
  };

  // Add Item to "What You Get"
  const handleAddWhatYouGetItem = () => {
    if (!newGetItem.trim()) return;
    setFormData((prev) => ({
      ...prev,
      whatYouGet: [...(prev.whatYouGet || []), newGetItem.trim().slice(0, MAX_WHAT_YOU_GET_LENGTH)]
    }));
    setFormErrors((prev) => (prev.whatYouGet ? { ...prev, whatYouGet: '' } : prev));
    setNewGetItem('');
  };

  const handleStartEditingWhatYouGetItem = (index, item) => {
    setEditingGetItemIndex(index);
    setEditingGetItemValue(item);
  };

  const handleSaveWhatYouGetItem = () => {
    const item = editingGetItemValue.trim();
    if (editingGetItemIndex == null || !item) return;
    setFormData((prev) => ({
      ...prev,
      whatYouGet: prev.whatYouGet.map((existing, index) =>
        index === editingGetItemIndex ? item.slice(0, MAX_WHAT_YOU_GET_LENGTH) : existing
      )
    }));
    setFormErrors((prev) => (prev.whatYouGet ? { ...prev, whatYouGet: '' } : prev));
    setEditingGetItemIndex(null);
    setEditingGetItemValue('');
  };

  const handleCancelEditingWhatYouGetItem = () => {
    setEditingGetItemIndex(null);
    setEditingGetItemValue('');
  };

  const handleRemoveWhatYouGetItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      whatYouGet: prev.whatYouGet.filter((_, i) => i !== index)
    }));
    setFormErrors((prev) => (prev.whatYouGet ? { ...prev, whatYouGet: '' } : prev));
    handleCancelEditingWhatYouGetItem();
  };

  // Open Create Dialog
  const handleOpenCreate = () => {
    setIsEditing(false);
    setFormErrors({});
    setFormData({
      ...EMPTY_PROMOTION_FORM,
      id: `promo-${Date.now()}`,
      cityName: cities[0]?.name || 'Berlin',
      cityId: cities[0]?.id || 'berlin-01',
      pictures: [],
      picturesKeep: [],
      picturesRemove: []
    });
    setNewGetItem('');
    handleCancelEditingWhatYouGetItem();
    setOpenDialog(true);
  };

  // Helper to resolve Promotion Type Name and ID when opening edit dialog
  const resolvePromoTypeAndId = (promo) => {
    let rawVal =
      promo.type ||
      promo.promotionType ||
      promo.promotionTypeName ||
      promo.typeName ||
      promo.PromotionType ||
      '';
    let rawId =
      promo.promotionTypeId ||
      promo.PromotionTypeId ||
      promo.typeId ||
      '';

    if (rawId && Array.isArray(promotionTypes)) {
      const found = promotionTypes.find((t) => {
        if (typeof t === 'object' && t !== null) {
          return String(t.id) === String(rawId) || String(t.typeId) === String(rawId);
        }
        return false;
      });
      if (found && typeof found === 'object') {
        return {
          type: found.name || found.type || found.label || String(rawVal),
          promotionTypeId: parseInt(found.id || rawId, 10) || 1
        };
      }
    }

    if (rawVal && Array.isArray(promotionTypes)) {
      const found = promotionTypes.find((t) => {
        if (typeof t === 'object' && t !== null) {
          return (t.name || t.type || t.label) === rawVal;
        }
        return String(t) === String(rawVal);
      });
      if (found && typeof found === 'object') {
        return {
          type: found.name || found.type || found.label || String(rawVal),
          promotionTypeId: parseInt(found.id, 10) || parseInt(rawId, 10) || 1
        };
      }
    }

    return {
      type: String(rawVal || ''),
      promotionTypeId: parseInt(rawId, 10) || 1
    };
  };

  // Open Edit Dialog
  const handleOpenEdit = (promo) => {
    setIsEditing(true);
    setFormErrors({});
    const typeInfo = resolvePromoTypeAndId(promo);
    const existingImages = getAllPromoImages(promo);
    setFormData({
      id: promo.id,
      title: promo.title || promo.Title || '',
      description: promo.description || promo.Description || '',
      details: promo.details || promo.promotionDetails || '',
      cityId: promo.cityId || promo.CityId || '',
      cityName: promo.cityName || 'Berlin',
      type: typeInfo.type,
      promotionTypeId: typeInfo.promotionTypeId,
      bannerImage: promo.bannerImage || '',
      discountText: promo.discountText || promo.DiscountText || '',
      discountCoupon: promo.discountCoupon || promo.DiscountCoupon || '',
      promotionDate: promo.promotionDate || promo.PromotionDate || new Date().toISOString().split('T')[0],
      promotionTime: promo.promotionTime || promo.PromotionTime || '14:00',
      webUrl: promo.webUrl || promo.WebUrl || '',
      sharingUrl: promo.sharingUrl || promo.SharingUrl || '',
      couponTitle: promo.couponTitle || promo.CouponTitle || '',
      ownerName: promo.ownerName || promo.OwnerName || '',
      termsAndConditions: promo.termsAndConditions || promo.TermsAndConditions || '',
      targetedAudience: promo.targetedAudience || promo.targetAudience || '',
      callToAction: promo.callToAction || promo.ctaText || '',
      whatYouGet: Array.isArray(promo.whatYouGet) ? promo.whatYouGet : [],
      pictures: [],
      picturesKeep: existingImages,
      picturesRemove: [],
      active: promo.active ?? true
    });
    setNewGetItem('');
    handleCancelEditingWhatYouGetItem();
    setOpenDialog(true);
  };

  // Validate Entire Form
  const validateEntireForm = () => {
    const errors = {
      title: validatePromotionField('title', formData.title, t),
      description: validatePromotionField('description', formData.description, t),
      cityName: validatePromotionField('cityName', formData.cityName, t),
      type: validatePromotionField('type', formData.type, t),
      promotionDate: validatePromotionField('promotionDate', formData.promotionDate, t),
      promotionTime: validatePromotionField('promotionTime', formData.promotionTime, t),
      webUrl: validatePromotionField('webUrl', formData.webUrl, t),
      sharingUrl: validatePromotionField('sharingUrl', formData.sharingUrl, t),
      details: validatePromotionField('details', formData.details, t),
      discountText: validatePromotionField('discountText', formData.discountText, t),
      couponTitle: validatePromotionField('couponTitle', formData.couponTitle, t),
      termsAndConditions: validatePromotionField('termsAndConditions', formData.termsAndConditions, t),
      whatYouGet: (formData.whatYouGet || []).some((item) => String(item ?? '').length > MAX_WHAT_YOU_GET_LENGTH)
        ? t('pages.promotions.validation.maxLength', { max: MAX_WHAT_YOU_GET_LENGTH })
        : ''
    };
    setFormErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  // Save Promotion Submit
  const handleSavePromotion = async () => {
    if (!validateEntireForm()) {
      return;
    }

    try {
      if (isEditing) {
        const res = await promotionsAPI.updatePromotion(formData.id, formData);
        const updatedData = res.data?.data || res.data || formData;
        setPromotions((prev) => {
          const updated = prev.map((p) => (p.id === formData.id ? { ...p, ...updatedData } : p));
          saveToLocal(updated);
          return updated;
        });
        toast.success(t('pages.promotions.toast.updateSuccess'));
      } else {
        const res = await promotionsAPI.createPromotion(formData);
        const createdData = res.data?.data || res.data || { ...formData, id: `promo-${Date.now()}` };
        setPromotions((prev) => {
          const updated = [createdData, ...prev];
          saveToLocal(updated);
          return updated;
        });
        toast.success(t('pages.promotions.toast.createSuccess'));
      }
      setOpenDialog(false);
    } catch (error) {
      const apiMsg = error.response?.data?.message || error.response?.data?.title || error.message || t('pages.promotions.toast.saveFailed');
      toast.error(typeof apiMsg === 'string' ? apiMsg : t('pages.promotions.toast.saveFailed'));
    }
  };

  // Toggle Active Status
  const handleToggleActive = async (promo) => {
    const newStatus = !promo.active;
    try {
      await promotionsAPI.togglePromotionStatus(promo.id, newStatus);
      setPromotions((prev) => {
        const updated = prev.map((p) => (p.id === promo.id ? { ...p, active: newStatus } : p));
        saveToLocal(updated);
        return updated;
      });
      toast.success(newStatus ? t('pages.promotions.toast.activated') : t('pages.promotions.toast.deactivated'));
    } catch (error) {
      const apiMsg = error.response?.data?.message || error.response?.data?.title || error.message || t('pages.promotions.toast.statusFailed');
      toast.error(typeof apiMsg === 'string' ? apiMsg : t('pages.promotions.toast.statusFailed'));
    }
  };

  // Delete Promotion
  const handleDeletePromotion = async () => {
    if (!deleteId) return;
    try {
      await promotionsAPI.deletePromotion(deleteId);
      setPromotions((prev) => {
        const updated = prev.filter((p) => p.id !== deleteId);
        saveToLocal(updated);
        return updated;
      });
      toast.success(t('pages.promotions.toast.deleteSuccess'));
    } catch (error) {
      const apiMsg = error.response?.data?.message || error.response?.data?.title || error.message || t('pages.promotions.toast.deleteFailed');
      toast.error(typeof apiMsg === 'string' ? apiMsg : t('pages.promotions.toast.deleteFailed'));
    } finally {
      setDeleteId(null);
    }
  };

  // Filtered List
  const filteredPromotions = useMemo(() => {
    return filterPromotionsList(promotions, cityFilter, typeFilter, statusFilter, searchTerm);
  }, [promotions, cityFilter, typeFilter, statusFilter, searchTerm]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header Banner Container (Matching User Screenshot UI) */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 4,
          borderRadius: '20px',
          border: '1px solid rgba(255, 140, 66, 0.25)',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.5rem', sm: '1.85rem' },
              color: '#1F2937',
              letterSpacing: '-0.02em',
              mb: 0.5
            }}
          >
            {t('pages.promotions.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
            {t('pages.promotions.subtitle')}
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleOpenCreate}
          sx={{
            backgroundColor: '#FF7A29',
            color: '#FFFFFF',
            px: 3,
            py: 1.2,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.95rem',
            boxShadow: '0 4px 14px rgba(255, 122, 41, 0.4)',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: '#E0621B',
              boxShadow: '0 6px 18px rgba(255, 122, 41, 0.5)'
            }
          }}
        >
          {t('pages.promotions.addBtn')}
        </Button>
      </Paper>

      {/* Filter and Search Bar */}
      <Box sx={{ mb: 4, maxWidth: '800px' }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Input Field */}
          <Grid item xs={12} sm={5} md={5}>
            <TextField
              fullWidth
              placeholder={t('pages.promotions.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '52px',
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  fontSize: '0.95rem',
                  '& fieldset': { borderColor: 'rgba(30, 58, 69, 0.22)' }
                }
              }}
            />
          </Grid>

          {/* Select Dropdown Field (Approval Status) */}
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth>
              <Select
                displayEmpty
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                renderValue={(selected) => {
                  if (selected === 'all') return <Typography sx={{ color: '#475569', fontSize: '0.95rem' }}>{t('pages.promotions.filter.statusAll')}</Typography>;
                  if (selected === 'active') return t('pages.promotions.filter.statusActive');
                  if (selected === 'inactive') return t('pages.promotions.filter.statusInactive');
                  return selected;
                }}
                sx={{
                  height: '52px',
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  fontSize: '0.95rem',
                  '& fieldset': { borderColor: 'rgba(30, 58, 69, 0.22)' }
                }}
              >
                <MenuItem value="all">{t('pages.promotions.filter.statusAll')}</MenuItem>
                <MenuItem value="active">{t('pages.promotions.filter.statusActive')}</MenuItem>
                <MenuItem value="inactive">{t('pages.promotions.filter.statusInactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filter Button with Icon */}
          <Grid item xs={12} sm={3} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterAltOffIcon sx={{ color: '#2563EB' }} />}
              onClick={() => {
                setSearchTerm('');
                setCityFilter('all');
                setTypeFilter('all');
                setStatusFilter('all');
              }}
              sx={{
                height: '52px',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                color: '#2563EB',
                borderColor: '#93C5FD',
                '&:hover': {
                  borderColor: '#2563EB',
                  backgroundColor: '#EFF6FF'
                }
              }}
            >
              {t('pages.promotions.filter.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Content Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: GS_COLORS.orange }} />
        </Box>
      ) : filteredPromotions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: '16px',
            border: `1px dashed ${GS_COLORS.border}`,
            backgroundColor: GS_COLORS.orangeLight
          }}
        >
          <TagIcon sx={{ fontSize: 60, color: GS_COLORS.orange, mb: 2 }} />
          <Typography variant="h6" color="text.primary" fontWeight={600} gutterBottom>
            {t('pages.promotions.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('pages.promotions.empty.hint')}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleOpenCreate}
            sx={{ borderRadius: '12px', textTransform: 'none', borderColor: GS_COLORS.orange, color: GS_COLORS.orangeDark }}
          >
            {t('pages.promotions.addBtn')}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredPromotions.map((promo) => (
            <Grid item xs={12} sm={6} md={4} key={promo.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '16px',
                  border: `1px solid ${GS_COLORS.border}`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(30, 58, 69, 0.1)',
                    borderColor: GS_COLORS.orange
                  }
                }}
              >
                {/* Card Top: Banner Image / Clean Horizontal Scroll Gallery */}
                <Box sx={{ position: 'relative', height: 180, backgroundColor: '#FFF7ED', overflow: 'hidden' }}>
                  <PromoCardGallery promo={promo} t={t} />

                  {/* Badges on Banner */}
                  <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 1, zIndex: 3, pointerEvents: 'none' }}>
                    <Chip
                      label={getPromoTypeName(promo, promotionTypes, t)}
                      size="small"
                      sx={{
                        backgroundColor: '#1E3A45',
                        color: '#FFFFFF',
                        fontWeight: 800,
                        fontSize: '11px',
                        borderRadius: '6px'
                      }}
                    />
                    {promo.discountText && (
                      <Chip
                        label={promo.discountText}
                        size="small"
                        sx={{
                          backgroundColor: '#FF7A29',
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '11px',
                          borderRadius: '6px'
                        }}
                      />
                    )}
                  </Box>

                  {/* Top-Right Badges Stack (Swapped Order: Approval Status Top, Active Status Bottom) */}
                  <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 3, pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: 0.6, alignItems: 'flex-end' }}>
                    {/* 1. Approval Status Pill (Top) */}
                    {(() => {
                      const { raw, label: statusLabel } = getPromoApprovalStatus(promo, t);
                      const isApproved = raw.includes('APPROVED');
                      const isPending = raw.includes('PENDING');
                      return (
                        <Chip
                          label={statusLabel}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: '10.5px',
                            letterSpacing: '0.04em',
                            borderRadius: '14px',
                            px: 0.5,
                            bgcolor: isApproved ? '#D1FAE5' : (isPending ? '#FEF3C7' : '#FEE2E2'),
                            border: `1px solid ${isApproved ? '#6EE7B7' : (isPending ? '#FDE68A' : '#FCA5A5')}`,
                            color: isApproved ? '#059669' : (isPending ? '#D97706' : '#DC2626')
                          }}
                        />
                      );
                    })()}

                    {/* 2. Active / Inactive Status Pill (Bottom) */}
                    <Chip
                      label={promo.active ? t('pages.promotions.card.active') : t('pages.promotions.card.inactive')}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: '11px',
                        borderRadius: '14px',
                        px: 0.5,
                        bgcolor: promo.active ? '#DCFCE7' : '#FFEBEE',
                        border: `1px solid ${promo.active ? '#86EFAC' : '#FFCDD2'}`,
                        color: promo.active ? '#15803D' : '#E53935'
                      }}
                    />
                  </Box>
                </Box>

                {/* Card Body */}
                <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1, color: GS_COLORS.textPrimary }}>
                    {promo.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#475569',
                      mb: 2,
                      width: '100%',
                      wordBreak: 'break-word',
                      fontSize: '0.92rem',
                      lineHeight: 1.55,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {promo.description}
                  </Typography>

                  <Stack spacing={1.2} sx={{ fontSize: '0.85rem', color: GS_COLORS.textPrimary }}>
                    {/* City (Left) & Date (Right) Side-by-Side Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <LocationIcon sx={{ fontSize: 16, color: GS_COLORS.orange }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                          {t('pages.promotions.card.city')}: {(() => {
                            if (promo.cityName && isNaN(Number(promo.cityName))) return promo.cityName;
                            const matched = cities.find((c) => String(c.id) === String(promo.cityId || promo.cityName));
                            return matched ? matched.name : (promo.cityName || promo.cityId || 'Berlin');
                          })()}
                        </Typography>
                      </Box>

                      {(() => {
                        const pDate = getPromoDateDisplay(promo);
                        const pTime = getPromoTimeDisplay(promo);
                        if (!pDate && !pTime) return null;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <CalendarIcon sx={{ fontSize: 15, color: '#94A3B8' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                              {pDate}{pTime ? ` • ${pTime}` : ''}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>

                    {/* COUPON BOX */}
                    {promo.discountCoupon && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#FFF7ED',
                          px: 1.5,
                          py: 0.8,
                          borderRadius: '10px',
                          border: '1px dashed #FF7A29'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TagIcon sx={{ fontSize: 16, color: '#D95300' }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#D95300' }}>
                            {t('pages.promotions.card.coupon')}: {promo.discountCoupon}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* CALL TO ACTION BOX */}
                    {(() => {
                      const ctaText = getCtaForLocale(promo, locale);
                      if (!ctaText) return null;
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#FFF5EF',
                            border: '1px solid #FF8C42',
                            borderRadius: '16px',
                            px: 2,
                            py: 0.9,
                            mt: 0.5,
                            width: '100%'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CampaignIcon sx={{ fontSize: 20, color: '#FF7A29' }} />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#D95300', fontSize: '0.9rem' }}>
                              {ctaText}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })()}
                  </Stack>


                </CardContent>

                <Divider sx={{ borderColor: GS_COLORS.border }} />

                {/* CARD ACTIONS */}
                <CardActions sx={{ justifyContent: 'flex-end', px: 2.5, py: 1.5 }}>
                  <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>

                    {/* 1. Toggle Status Circular Button */}
                    <Tooltip title={promo.active ? t('pages.promotions.card.deactivate') : t('pages.promotions.card.activate')}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(promo)}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          backgroundColor: promo.active ? '#E8F5E9' : '#FFEBEE',
                          border: `1px solid ${promo.active ? '#A5D6A7' : '#FFCDD2'}`,
                          color: promo.active ? '#2E7D32' : '#E53935',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: promo.active ? '#C8E6C9' : '#FFCDD2',
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        {promo.active ? (
                          <ToggleOnIcon sx={{ fontSize: 22, color: '#2E7D32' }} />
                        ) : (
                          <ToggleOffIcon sx={{ fontSize: 22, color: '#E53935' }} />
                        )}
                      </IconButton>
                    </Tooltip>

                    {/* Card View Preview Circular Button */}
                    <Tooltip title={t('pages.promotions.card.cardView') !== 'pages.promotions.card.cardView' ? t('pages.promotions.card.cardView') : 'Card View'}>
                      <IconButton
                        size="small"
                        onClick={() => setCardPreviewPromo(promo)}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(6, 57, 55, 0.08)',
                          border: '1px solid rgba(6, 57, 55, 0.25)',
                          color: '#063937',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: '#063937',
                            color: '#ffffff',
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        <CardViewIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>

                    {/* Mobile Mockup Preview Circular Button */}
                    <Tooltip title={t('pages.promotions.card.mobilePreview') !== 'pages.promotions.card.mobilePreview' ? t('pages.promotions.card.mobilePreview') : 'Mobile Preview'}>
                      <IconButton
                        size="small"
                        onClick={() => setMobilePreviewPromo(promo)}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          backgroundColor: '#E3F2FD',
                          border: '1px solid #90CAF9',
                          color: '#1976D2',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: '#BBDEFB',
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        <SmartphoneIcon sx={{ fontSize: 19, color: '#1976D2' }} />
                      </IconButton>
                    </Tooltip>

                    {/* 2. Edit Circular Button */}
                    <Tooltip title={t('pages.promotions.card.edit')}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(promo)}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          backgroundColor: '#FFF3E0',
                          border: '1px solid #FFCC80',
                          color: '#F57C00',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: '#FFE0B2',
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        <EditIcon sx={{ fontSize: 19, color: '#F57C00' }} />
                      </IconButton>
                    </Tooltip>

                    {/* 3. Delete Circular Button */}
                    <Tooltip title={t('pages.promotions.card.delete')}>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteId(promo.id)}
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          backgroundColor: '#FFEBEE',
                          border: '1px solid #FFCDD2',
                          color: '#D32F2F',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: '#FFCDD2',
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 19, color: '#D32F2F' }} />
                      </IconButton>
                    </Tooltip>

                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* CREATE / EDIT PROMOTION FORM DIALOG */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            '& .MuiFormLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
            '& .MuiInputLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={700} color={GS_COLORS.textPrimary}>
            {isEditing ? t('pages.promotions.editTitle') : t('pages.promotions.addTitle')}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderColor: GS_COLORS.border,
            py: 3,
            '& .MuiFormLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
            '& .MuiInputLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
          }}
        >
          <Grid container spacing={2.5}>
            {/* 1. Promotion Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('pages.promotions.fields.title')}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                error={Boolean(formErrors.title)}
                inputProps={{ maxLength: PROMOTION_FIELD_MAX_LENGTHS.title }}
                helperText={formErrors.title}
                required
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 2. Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('pages.promotions.fields.description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                error={Boolean(formErrors.description)}
                inputProps={{ maxLength: PROMOTION_FIELD_MAX_LENGTHS.description }}
                helperText={formErrors.description}
                required
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 3. Promotion Details (optional) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('pages.promotions.fields.details')}
                name="details"
                value={formData.details}
                onChange={handleInputChange}
                inputProps={{ maxLength: PROMOTION_FIELD_MAX_LENGTHS.details }}
                error={Boolean(formErrors.details)}
                helperText={formErrors.details}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 4. City */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={Boolean(formErrors.cityName)} sx={formFieldSx}>
                <InputLabel>{t('pages.promotions.fields.city')}</InputLabel>
                <Select
                  name="cityName"
                  value={formData.cityName || ''}
                  label={t('pages.promotions.fields.city')}
                  onChange={(e) => {
                    const selectedCity = cities.find((c) => c.name === e.target.value || c.id === e.target.value);
                    const cId = selectedCity?.id ? (parseInt(selectedCity.id, 10) || selectedCity.id) : 1;
                    setFormData((prev) => ({
                      ...prev,
                      cityName: e.target.value,
                      cityId: cId
                    }));
                    if (formErrors.cityName) {
                      setFormErrors((prev) => ({ ...prev, cityName: '' }));
                    }
                  }}
                >
                  {cities.map((c) => (
                    <MenuItem key={c.id || c.name} value={c.name || c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.cityName && <FormHelperText>{formErrors.cityName}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* 5. Promotion Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={Boolean(formErrors.type)} sx={formFieldSx}>
                <InputLabel>{t('pages.promotions.fields.type')}</InputLabel>
                <Select
                  name="type"
                  value={formData.type || ''}
                  label={t('pages.promotions.fields.type')}
                  onChange={(e) => {
                    const val = e.target.value;
                    const selectedTypeObj = promotionTypes.find((t) => {
                      if (typeof t === 'object' && t !== null) {
                        return (t.name || t.type || t.label || String(t.id)) === val;
                      }
                      return String(t) === val;
                    });
                    const typeIdNum = selectedTypeObj && typeof selectedTypeObj === 'object' && selectedTypeObj.id
                      ? parseInt(selectedTypeObj.id, 10) || 1
                      : 1;
                    setFormData((prev) => ({
                      ...prev,
                      type: val,
                      promotionTypeId: typeIdNum
                    }));
                    if (formErrors.type) {
                      setFormErrors((prev) => ({ ...prev, type: '' }));
                    }
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography sx={{ color: '#94A3B8' }}>{t('pages.promotions.fields.type')}</Typography>;
                    }
                    return formatPromoType(selected, t);
                  }}
                >
                  <MenuItem value="" disabled sx={{ color: '#94A3B8' }}>
                    {t('pages.promotions.fields.type')}
                  </MenuItem>
                  {promotionTypes.map((typeObj) => {
                    const typeVal = typeof typeObj === 'object' && typeObj !== null
                      ? (typeObj.name || typeObj.type || typeObj.label || String(typeObj.id))
                      : String(typeObj);
                    return (
                      <MenuItem key={typeVal} value={typeVal}>
                        {formatPromoType(typeVal, t)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {formErrors.type && <FormHelperText>{formErrors.type}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* 7. Discount Text (optional) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.promotions.fields.discountText')}
                name="discountText"
                placeholder={t('pages.promotions.placeholders.discountText')}
                value={formData.discountText}
                onChange={handleInputChange}
                inputProps={{ maxLength: PROMOTION_FIELD_MAX_LENGTHS.discountText }}
                error={Boolean(formErrors.discountText)}
                helperText={formErrors.discountText}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 8. Discount Coupon (optional) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.promotions.fields.discountCoupon')}
                name="discountCoupon"
                placeholder={t('pages.promotions.placeholders.discountCoupon')}
                value={formData.discountCoupon}
                onChange={handleInputChange}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* Coupon Title (optional) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.promotions.fields.couponTitle')}
                name="couponTitle"
                placeholder={t('pages.promotions.placeholders.couponTitle')}
                value={formData.couponTitle}
                onChange={handleInputChange}
                inputProps={{ maxLength: PROMOTION_FIELD_MAX_LENGTHS.couponTitle }}
                error={Boolean(formErrors.couponTitle)}
                helperText={formErrors.couponTitle}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 9. Promotion Date (YYYY-MM-DD) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label={t('pages.promotions.fields.promotionDate')}
                name="promotionDate"
                value={formData.promotionDate}
                onChange={handleInputChange}
                error={Boolean(formErrors.promotionDate)}
                helperText={formErrors.promotionDate}
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10) }}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 10. Promotion Time (HH:mm) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label={t('pages.promotions.fields.promotionTime')}
                name="promotionTime"
                value={formData.promotionTime}
                onChange={handleInputChange}
                error={Boolean(formErrors.promotionTime)}
                helperText={formErrors.promotionTime}
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 11. Web URL (optional with validation) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.promotions.fields.webUrl')}
                name="webUrl"
                placeholder={t('pages.promotions.placeholders.webUrl')}
                value={formData.webUrl}
                onChange={handleInputChange}
                error={Boolean(formErrors.webUrl)}
                helperText={formErrors.webUrl}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* Sharing URL & Owner Name Side-by-Side Row */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.promotions.fields.sharingUrl')}
                name="sharingUrl"
                placeholder={t('pages.promotions.placeholders.sharingUrl')}
                value={formData.sharingUrl}
                onChange={handleInputChange}
                error={Boolean(formErrors.sharingUrl)}
                helperText={formErrors.sharingUrl}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.promotions.fields.ownerName')}
                name="ownerName"
                value={formData.ownerName}
                onChange={handleInputChange}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* Pictures (multiple image upload) */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: GS_COLORS.textPrimary, mb: 1 }}>
                {t('pages.promotions.fields.bannerImage')}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  borderColor: GS_COLORS.borderDark,
                  backgroundColor: GS_COLORS.orangeLight,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5
                }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  {/* Kept Existing Pictures */}
                  {Array.isArray(formData.picturesKeep) && formData.picturesKeep.map((picUrl, idx) => (
                    <Box key={`keep-${idx}`} sx={{ position: 'relative', width: 110, height: 80, borderRadius: '10px', overflow: 'hidden', border: '1px solid #FF8C42' }}>
                      <img src={picUrl} alt={`Keep ${idx + 1}`} onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <IconButton
                        size="small"
                        onClick={() => removePictureKeepItem(idx)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          p: 0.2,
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}

                  {/* Newly Uploaded Picture Files */}
                  {Array.isArray(formData.pictures) && formData.pictures.map((pic, idx) => {
                    const srcUrl = typeof pic === 'string' ? pic : (typeof File !== 'undefined' && pic instanceof File ? URL.createObjectURL(pic) : '');
                    return (
                      <Box key={`new-${idx}`} sx={{ position: 'relative', width: 110, height: 80, borderRadius: '10px', overflow: 'hidden', border: '1px solid #1E3A45' }}>
                        {srcUrl ? (
                          <img src={srcUrl} alt={`New ${idx + 1}`} onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
                            <Typography variant="caption">{pic.name || `Image ${idx + 1}`}</Typography>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => removePictureItem(idx)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            p: 0.2,
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}

                  <Button
                    component="label"
                    variant="outlined"
                    sx={{
                      width: 110,
                      height: 80,
                      borderRadius: '10px',
                      borderStyle: 'dashed',
                      borderColor: GS_COLORS.orange,
                      backgroundColor: '#fff',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      textTransform: 'none',
                      color: GS_COLORS.orangeDark
                    }}
                  >
                    <AddPhotoIcon sx={{ fontSize: 24, color: GS_COLORS.orange }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t('pages.promotions.fields.uploadBtn')}
                    </Typography>
                    <input type="file" accept="image/*" multiple hidden onChange={handlePicturesUpload} />
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* 13. Terms And Conditions (optional) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('pages.promotions.fields.terms')}
                name="termsAndConditions"
                value={formData.termsAndConditions}
                onChange={handleInputChange}
                inputProps={{ maxLength: PROMOTION_FIELD_MAX_LENGTHS.termsAndConditions }}
                error={Boolean(formErrors.termsAndConditions)}
                helperText={formErrors.termsAndConditions}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 14. Targeted Audience (optional) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={formFieldSx}>
                <InputLabel id="promotion-target-audience-label">{t('pages.promotions.fields.targetedAudience')}</InputLabel>
                <Select
                  labelId="promotion-target-audience-label"
                  name="targetedAudience"
                  value={formData.targetedAudience || ''}
                  label={t('pages.promotions.fields.targetedAudience')}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">{t('pages.promotions.audience.select')}</MenuItem>
                  {TARGET_AUDIENCES.map((audience) => (
                    <MenuItem key={audience.value} value={audience.value}>
                      {t(`pages.promotions.audience.${audience.translationKey}`)}
                    </MenuItem>
                  ))}
                  {formData.targetedAudience && !TARGET_AUDIENCES.some((audience) => audience.value === formData.targetedAudience) && (
                    <MenuItem value={formData.targetedAudience}>{formData.targetedAudience}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* 15. Call To Action (Single field with Max 100 limit & 0/100 counter) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('pages.promotions.cta.title')}
                name="callToAction"
                placeholder={t('pages.promotions.placeholders.cta')}
                value={formData.callToAction || ''}
                onChange={handleInputChange}
                inputProps={{ maxLength: 100 }}
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* 16. What You Get (optional list section) */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: '12px',
                  borderColor: GS_COLORS.borderDark,
                  backgroundColor: GS_COLORS.orangeLight
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: GS_COLORS.textPrimary }}>
                    {t('pages.promotions.fields.whatYouGet')}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddWhatYouGetItem}
                    sx={{ textTransform: 'none', fontWeight: 600, color: GS_COLORS.orangeDark }}
                  >
                    {t('pages.promotions.fields.addItem')}
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('pages.promotions.placeholders.whatYouGetItem')}
                    value={newGetItem}
                    onChange={(e) => setNewGetItem(e.target.value.slice(0, MAX_WHAT_YOU_GET_LENGTH))}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddWhatYouGetItem())}
                    inputProps={{ maxLength: MAX_WHAT_YOU_GET_LENGTH }}
                    sx={formFieldSx}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddWhatYouGetItem}
                    sx={{ borderRadius: '10px', textTransform: 'none', px: 3, backgroundColor: GS_COLORS.orange, '&:hover': { backgroundColor: GS_COLORS.orangeDark } }}
                  >
                    {t('pages.promotions.fields.add')}
                  </Button>
                </Box>

                {formErrors.whatYouGet && (
                  <FormHelperText error sx={{ mb: 1 }}>{formErrors.whatYouGet}</FormHelperText>
                )}

                {formData.whatYouGet.length > 0 && (
                  <Stack spacing={1}>
                    {formData.whatYouGet.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1,
                          backgroundColor: GS_COLORS.white,
                          borderRadius: '8px',
                          border: `1px solid ${GS_COLORS.border}`
                        }}
                      >
                        {editingGetItemIndex === index ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editingGetItemValue}
                            onChange={(e) => setEditingGetItemValue(e.target.value.slice(0, MAX_WHAT_YOU_GET_LENGTH))}
                            inputProps={{ maxLength: MAX_WHAT_YOU_GET_LENGTH }}
                            sx={formFieldSx}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: GS_COLORS.textPrimary, fontWeight: 500, flex: 1 }}>
                            • {item}
                          </Typography>
                        )}
                        {editingGetItemIndex === index ? (
                          <>
                            <IconButton size="small" aria-label={t('pages.promotions.fields.saveItem')} onClick={handleSaveWhatYouGetItem} disabled={!editingGetItemValue.trim()}>
                              <CheckIcon sx={{ color: '#16A34A' }} />
                            </IconButton>
                            <IconButton size="small" aria-label={t('pages.promotions.fields.cancelEditItem')} onClick={handleCancelEditingWhatYouGetItem}>
                              <CloseIcon sx={{ color: GS_COLORS.textMuted }} />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton size="small" aria-label={t('pages.promotions.fields.editItem')} onClick={() => handleStartEditingWhatYouGetItem(index, item)}>
                            <EditIcon sx={{ color: GS_COLORS.orangeDark }} />
                          </IconButton>
                        )}
                        <IconButton size="small" aria-label={t('pages.promotions.fields.removeItem')} onClick={() => handleRemoveWhatYouGetItem(index)}>
                          <CloseIcon fontSize="small" sx={{ color: '#EF4444' }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* 17. Active Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="active"
                    checked={Boolean(formData.active)}
                    onChange={handleInputChange}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: GS_COLORS.orange
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: GS_COLORS.orange
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontWeight: 600, color: GS_COLORS.textPrimary }}>
                    {t('pages.promotions.fields.active')}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none', color: GS_COLORS.textMuted, px: 3 }}>
            {t('pages.promotions.btn.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePromotion}
            sx={{
              backgroundColor: GS_COLORS.orange,
              color: '#fff',
              px: 4,
              py: 1,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(255, 122, 41, 0.3)',
              '&:hover': { backgroundColor: GS_COLORS.orangeDark }
            }}
          >
            {isEditing ? t('pages.promotions.btn.save') : t('pages.promotions.btn.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW PROMOTION DETAILS DIALOG */}
      <Dialog
        open={Boolean(viewPromo)}
        onClose={() => setViewPromo(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        {viewPromo && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={700} color={GS_COLORS.textPrimary}>
                {viewPromo.title}
              </Typography>
              <IconButton onClick={() => setViewPromo(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ py: 3, borderColor: GS_COLORS.border }}>
              {(() => {
                const detailImages = getAllPromoImages(viewPromo);
                if (detailImages.length === 0) return null;
                return (
                  <Box
                    sx={{
                      mb: 2.5,
                      display: 'flex',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      borderRadius: '12px',
                      height: 220,
                      scrollSnapType: 'x mandatory',
                      '&::-webkit-scrollbar': { height: '6px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#FF7A29', borderRadius: '4px' }
                    }}
                  >
                    {detailImages.map((imgUrl, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          minWidth: detailImages.length > 1 ? '90%' : '100%',
                          width: detailImages.length > 1 ? '90%' : '100%',
                          height: '100%',
                          flexShrink: 0,
                          scrollSnapAlign: 'start',
                          position: 'relative',
                          borderRight: detailImages.length > 1 && idx < detailImages.length - 1 ? '2px solid #fff' : 'none'
                        }}
                      >
                        <img src={imgUrl} alt={`${viewPromo.title || 'Promotion'} ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} />
                        {detailImages.length > 1 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.65)',
                              color: '#fff',
                              px: 1,
                              py: 0.3,
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 700
                            }}
                          >
                            {idx + 1} / {detailImages.length}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              <Typography variant="body1" paragraph color="text.secondary">
                {viewPromo.description}
              </Typography>

              {viewPromo.discountCoupon && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: GS_COLORS.orangeLight,
                    px: 2,
                    py: 1,
                    borderRadius: '10px',
                    border: `1px dashed ${GS_COLORS.orange}`
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color={GS_COLORS.orangeDark}>
                    {t('pages.promotions.card.coupon')}: {viewPromo.discountCoupon}
                  </Typography>
                  <Tooltip title={t('pages.promotions.card.copyCoupon')}>
                    <IconButton size="small" onClick={() => handleCopyCoupon(viewPromo.discountCoupon)}>
                      <ContentCopyIcon fontSize="small" sx={{ color: GS_COLORS.orangeDark }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {viewPromo.callToAction && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color={GS_COLORS.textPrimary}>{t('pages.promotions.cta.title')}</Typography>
                  <Chip label={viewPromo.callToAction} sx={{ backgroundColor: GS_COLORS.orangeLight, color: GS_COLORS.orangeDark, fontWeight: 700, mt: 0.5 }} />
                </Box>
              )}

              {viewPromo.details && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color={GS_COLORS.textPrimary}>{t('pages.promotions.fields.detailsLabel')}</Typography>
                  <Typography variant="body2" color="text.secondary">{viewPromo.details}</Typography>
                </Box>
              )}

              {viewPromo.whatYouGet && viewPromo.whatYouGet.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color={GS_COLORS.textPrimary} gutterBottom>{t('pages.promotions.fields.whatYouGet')}</Typography>
                  <Stack spacing={0.5}>
                    {viewPromo.whatYouGet.map((it, idx) => (
                      <Typography key={idx} variant="body2" color="text.secondary">✓ {it}</Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              {viewPromo.termsAndConditions && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color={GS_COLORS.textPrimary}>{t('pages.promotions.fields.terms')}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewPromo.termsAndConditions}</Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewPromo(null)} variant="outlined" sx={{ borderRadius: '10px', textTransform: 'none', borderColor: GS_COLORS.borderDark, color: GS_COLORS.textPrimary }}>
                {t('pages.promotions.btn.close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.35rem', color: '#1E293B', pt: 3.5, px: 3.5, pb: 1.5 }}>
          {t('pages.promotions.deleteTitle')}
        </DialogTitle>
        <DialogContent sx={{ px: 3.5, py: 0 }}>
          <Typography sx={{ color: '#475569', fontSize: '0.96rem', lineHeight: 1.55 }}>
            {t('pages.promotions.confirmDelete')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3.5, pb: 3.5, pt: 3, gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setDeleteId(null)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#64748B',
              px: 2,
              py: 0.8,
              borderRadius: '10px',
              '&:hover': { bgcolor: '#F1F5F9', color: '#334155' }
            }}
          >
            {t('pages.promotions.btn.cancel')}
          </Button>
          <Button
            onClick={handleDeletePromotion}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              bgcolor: '#D92D20',
              color: '#FFFFFF',
              px: 2.8,
              py: 0.9,
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(217, 45, 32, 0.25)',
              '&:hover': { bgcolor: '#B42318', boxShadow: '0 4px 12px rgba(217, 45, 32, 0.35)' }
            }}
          >
            {t('pages.promotions.btn.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CARD VIEW PREVIEW DIALOG */}
      <PromotionCardViewDialog
        open={Boolean(cardPreviewPromo)}
        promo={cardPreviewPromo}
        onClose={() => setCardPreviewPromo(null)}
        onOpenMobilePreview={(p) => setMobilePreviewPromo(p)}
        t={t}
        locale={locale}
        promotionTypes={promotionTypes}
      />

      {/* MOBILE MOCKUP PREVIEW DIALOG */}
      <PromotionMobileMockupDialog
        open={Boolean(mobilePreviewPromo)}
        promo={mobilePreviewPromo}
        onClose={() => setMobilePreviewPromo(null)}
        t={t}
        locale={locale}
        promotionTypes={promotionTypes}
      />
    </Box>
  );
}
